/** Pure helpers behind FlowerCatalogPage.js's image upload/replace/remove
 * flow -- extracted so the actual decision rules are directly unit-testable
 * without touching Firebase, same convention as this project's other
 * admin-panel/src/*.test.js files (buildLocalizedTextPayload, entryDocId,
 * etc: pure logic next to the component that uses it). The Firebase Storage
 * calls themselves (uploadBytes/getDownloadURL/deleteObject) stay inline in
 * FlowerCatalogPage.js, matching every other page in this app -- only the
 * string-building and decision logic move here. */

/** Builds the Storage path for a flower's image: <entryId>/<time>-<name>,
 * matching CemeteriesPage.js's cemetery-photos/<cityId>/<id>/<time>-<name>
 * shape. `timestamp` defaults to Date.now() but is an explicit parameter so
 * a test can assert an exact path. */
export function buildFlowerImagePath(entryId, fileName, timestamp = Date.now()) {
  const safeName = String(fileName || '').replace(/[^a-zA-Z0-9._-]/g, '_');
  return `flower_catalog_photos/${entryId}/${timestamp}-${safeName}`;
}

/** Raster formats FlowerImage (mobile-app/lib/shared/widgets/flower_image.dart,
 * a plain CachedNetworkImage) can actually decode. SVG in particular is
 * explicitly excluded -- Codex finding: the file picker's `accept="image/*"`
 * and the Storage rule's `contentType.matches('image/.*')` both happily
 * admit `image/svg+xml`, which previews fine in a browser `<img>` tag but
 * CachedNetworkImage cannot decode on Flutter mobile, so the flower would
 * silently show only the error placeholder there despite a successful,
 * seemingly-fine admin-panel save. */
export const SUPPORTED_FLOWER_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

export function isSupportedFlowerImageType(mimeType) {
  return SUPPORTED_FLOWER_IMAGE_TYPES.includes(String(mimeType || '').toLowerCase());
}

/**
 * Decides what a flower's imageUrl/imagePath should become after a save,
 * and which (if any) previously-owned Storage object should be cleaned up
 * afterward -- the exact three-way choice createEntry()/saveEdit() make:
 * a new file was uploaded (replace), the admin explicitly removed the
 * image (clear), or neither (keep the existing image untouched, e.g. the
 * admin only changed the price/description).
 *
 * Never returns an `oldPathToDelete` for a path that isn't this flower's
 * own previous `currentImagePath` -- the caller can only ever end up
 * deleting an object this exact flower used to own, never another
 * flower's.
 *
 * @param {{currentImageUrl: string, currentImagePath: string,
 *   hasNewFile: boolean, uploadedImageUrl?: string,
 *   uploadedImagePath?: string, removed: boolean}} args
 * @return {{imageUrl: string, imagePath: string, oldPathToDelete: string}}
 */
export function resolveFlowerImageOnSave({
  currentImageUrl,
  currentImagePath,
  hasNewFile,
  uploadedImageUrl,
  uploadedImagePath,
  removed,
}) {
  if (hasNewFile) {
    return {
      imageUrl: uploadedImageUrl,
      imagePath: uploadedImagePath,
      oldPathToDelete:
        currentImagePath && currentImagePath !== uploadedImagePath ? currentImagePath : '',
    };
  }
  if (removed) {
    return {
      imageUrl: '',
      imagePath: '',
      oldPathToDelete: currentImagePath || '',
    };
  }
  // Neither replaced nor removed -- e.g. only price/description changed.
  // The existing image must survive untouched.
  return {
    imageUrl: currentImageUrl || '',
    imagePath: currentImagePath || '',
    oldPathToDelete: '',
  };
}
